import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-markup';  
import 'prismjs/themes/prism.css'; //Example style, you can use another

import ConditionalButton from "../../../layout/conditional-button";
import BaseModal from "../../../layout/modal";
import ValueSelect from "../../../layout/select";
import TestModal from "./test";
import ScrollToTopButton from "./scroll-to-top-button";
import CustomFieldsEditor from "./custom-fields-editor";

import { withNavigation } from "../../../utils/wrapper";
import { isGuid } from "../../../utils/string";
import {
    applyCustomValues,
    normalizeCustomFields,
    validateCustomFields,
} from "../../../utils/template-custom-fields";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getTemplate, save as saveTemplate, destroy, send as sendEmail} from "../../../actions/email.template";
import { get as getConfiguration } from "../../../actions/email.configuration";

const EMAIL_PATTERN = "^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$";

const EDITOR_STYLE = {
    fontFamily: '"Fira Code", Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: 14,
    lineHeight: 1.5,
    // Match Prism background so selection looks right
    background: 'transparent',
    whiteSpace: "pre",        //* keep \n, don’t soft-wrap */
    overflowX: "auto",        //* horizontal scroll when needed */
    overflowY: "auto",
    overflowWrap: "normal",   //* don’t break long words */
    wordBreak: "normal"
};

const CATEGORY_OPTIONS = [
    { value: "Events",   label: "Events" },
    { value: "Intakes",  label: "Intakes" },
    { value: "Payments", label: "Payments" },
    { value: "Security", label: "Security" }
];

const editorValue = (value) => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value;

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const cloneText = (value, fallback, uuid, maxLength = 60) => {
    const source = String(value || fallback || "Email Template").trim() || "Email Template";
    const suffix = ` (${String(uuid || "").slice(0, 8)})`;
    const prefix = "Copy of ";
    const room = Math.max(1, maxLength - prefix.length - suffix.length);
    return `${prefix}${source.slice(0, room).trim()}${suffix}`;
};

class EmailTemplateItem extends React.Component {
    
    constructor(props) {
        super(props);
        const existingUuid = props.params.uuid;

        this.props.loading(true);
        this.loading = true;

        this.state = {
            uuid: isGuid(existingUuid) ? existingUuid : uuidv4(),
            name: "[New Email Template]",
            category: CATEGORY_OPTIONS[0].value,
            criteria: "",
            template: "",
            from: "",
            replyTo: "",
            subject: "",
            html: "",
            customFields: [],
            configurations: [],
            modal: {
                test: { show: false },
                delete: { show: false },
            },
            tooltips: {
                test: "Test Template",
                pending: "This feature will be enabled after you save your changes"
            },
            errors: {},
            customFieldErrors: {},
            flags: 0
        };
        
        this.props.pageInfo({ title: "Email Template", breadcrumb: this.state?.name });
        this.changed = false;
    }

    get uuid() {
        return this.props.params.uuid;
    }

    get isNew() {
        return !isGuid(this.uuid);
    }

    get isUnsavedCopy() {
        return this.state.cloned === true;
    }

    get isNewOrUnsavedCopy() {
        return this.isNew || this.isUnsavedCopy;
    }

    loadData = async () => {
        this.props.loading(true);
        this.loading = true;

        const configurations = (await this.props.getConfiguration()) || [];

        if (isGuid(this.uuid)) {
            const templates = await this.props.getTemplate(this.uuid);
            const template = (templates && templates.length) ? templates[0] : {};
            this.props.pageInfo({ title: "Email Template", breadcrumb: template?.name });
            template.template = template.content;
            const configuration = template.configuration || {};
            template.criteria = editorValue(configuration.criteria);
            template.html = editorValue(template.html ?? template.content);
            template.customFields = normalizeCustomFields(configuration.customFields);

            delete template.content;
            delete template.configuration;
            
            this.setState({ ...template, configurations, cloned: false, clonedFromUuid: null });
        } else {
            this.props.pageInfo({ title: "Email Template", breadcrumb: "[New Email Template]" });
            this.setState({ configurations, cloned: false, clonedFromUuid: null }); // <-- add this
        }

        this.changed = false;
        this.loading = false;
        this.props.loading(false);
    };
    
    componentDidMount = () => {
        setTimeout(async () => {
            await this.loadData();
        }, 30);
    }

    componentDidUpdate(prevProps) {
        const prevUuid = prevProps?.params?.uuid ?? null;
        const nextUuid = this.props?.params?.uuid ?? null;

        if (prevUuid !== nextUuid) {
            this.loadData();
        }
    }

    onSaveClick = async () => {
        try {
            this.props.loading(true);
            this.loading = true;

            const {
                name,
                category,
                caption,
                from,
                replyTo,
                subject,
                html,
                uuid,
                criteria,
                customFields,
                flags,
                hidden
            } = this.state;

            const customFieldValidation = validateCustomFields(customFields);
            if (!customFieldValidation.valid) {
                this.setState({ customFieldErrors: customFieldValidation.errors });
                return;
            }

            const result = await this.props.saveTemplate({
                name,
                category,
                caption,
                from,
                replyTo,
                subject,
                html,
                uuid,
                configuration: {
                    criteria,
                    customFields: customFieldValidation.fields,
                },
                flags,
                hidden,
            });

            this.changed = false;
            this.props.navigate(`/dashboard/templates/email/${result.uuid ?? uuid}`);
        } finally {
            this.loading = false;
            this.props.loading(false);
        }
    }

    onTestClick = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                test: { show: true }
            }
        }));
    }

    onCloneClick = () => {
        const nextUuid = uuidv4();
        const name = cloneText(this.state.name, "Email Template", nextUuid);
        const caption = cloneText(this.state.caption || this.state.name, "Email Template", nextUuid, 255);

        this.setState({
            uuid: nextUuid,
            sourceUuid: null,
            name,
            caption,
            cloned: true,
            clonedFromUuid: this.state.uuid,
            errors: {},
            customFieldErrors: {}
        }, () => {
            this.changed = true;
            this.props.pageInfo({ title: "Email Template", breadcrumb: name });
        });
    }

    onTestOkModal = async (e) => {
        const dataset = applyCustomValues(e.dataset, e.custom, this.state.customFields);
        await this.props.sendEmail({name: this.state.name, configuration: e.configuration, json: dataset, recipient: e.to});
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                test: { show: false }
            }
        }));
    }

    onChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        });
        this.changed = true;
    }

    onCustomFieldsChange = (customFields) => {
        const validation = validateCustomFields(customFields);
        this.setState({
            customFields,
            customFieldErrors: validation.errors
        });
        this.changed = true;
    }
    
    onFlagChange = (checked, bit) => {
        this.setState(prev => {
            const flags = prev.flags ?? 0;
            const nextFlags = checked ? (flags | bit) : (flags & ~bit);
            return { flags: nextFlags };
        }, () => {
            this.changed = true;
        });
    }
    

    onCriteriaChange = (e) => {
        this.onChange({
            target: {
                name: "criteria",
                value: e
            }
        });
    }

    onHtmlChange = (e) => {
        this.onChange({
            target: {
                name: "html",
                value: e
            }
        });
    }

    onBlur = (e) => {
        let { name, value, required, type, min, max, minLength, maxLength, pattern } = e.target;

        let error = false;
        value = value ?? "";

        // required
        if (required && String(value).trim() === "") {
            error = true;
        }

        minLength = Number(minLength);
        maxLength = Number(maxLength);
        
        if (!error && Number.isFinite(minLength) && minLength > 0 && value.length < minLength) error = true;
        if (!error && Number.isFinite(maxLength) && maxLength > 0 && value.length > maxLength) error = true;

        // number constraints
        if (!error && type === "number" && value !== "") {
            const n = Number(value);
            if (!Number.isFinite(n)) error = true;

            const hasMin = min !== undefined && min !== "";
            const hasMax = max !== undefined && max !== "";
            if (!error && hasMin && n < Number(min)) error = true;
            if (!error && hasMax && n > Number(max)) error = true;
        }

        // pattern (if present)
        if (!error && pattern) {
            try {
                const re = new RegExp(`^(?:${pattern})$`);
                if (!re.test(value)) error = true;
            } catch {
                // bad pattern -> ignore
            }
        }

        this.setState(prev => {
            const next = { ...prev.errors };
            if (error) next[name] = true;
            else delete next[name];
            return { errors: next };
        });
    }

    onDeleteClick = (e) => {
        if (this.isNewOrUnsavedCopy) {
            this.props.navigate("/dashboard/templates");
            return;
        }

        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    title: "Delete template",
                    body: `This action will permanently delete the template named ${this.state.name}. If this template is in use, whatever function that template serves will fail to perform as expected.`,
                    show: true
                }
            }
        }));
    }
    

    onDeleteOkModal = (e) => {
        if (this.isNewOrUnsavedCopy) {
            this.props.navigate("/dashboard/templates");
            return;
        }

        this.props.destroy(this.state.uuid);
        this.onDeleteHideModal(e);
        this.props.navigate("/dashboard/templates");
    }

    onDeleteHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    show: false
                }
            }
        }));
    }

    onXmlHighlight = (e) => {
        return highlight(e, languages.xml, "xml");
    }

    onJsonHighlight = (e) => {
        return highlight(e, languages.json, "json");
    }

    hasFlag = (bit) => (((this.state.flags ?? 0) & bit) !== 0);

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton enabledVariant="outline-primary" tooltip={this.changed ? this.state.tooltips.pending : this.state.tooltips.test} display={true} enabled={!this.changed && !this.loading} onEnabledClick={this.onTestClick} icon="flash">Test</ConditionalButton>
                    <ConditionalButton enabledVariant="outline-primary" tooltip="Clone Template" display={true} enabled={!this.loading && !this.isNewOrUnsavedCopy} onEnabledClick={this.onCloneClick} icon="duplicate">Clone</ConditionalButton>
                    <ConditionalButton enabledVariant="outline-primary" tooltip="Save Template" display={true} enabled={!this.loading} onEnabledClick={this.onSaveClick} icon="save">Save</ConditionalButton>
                    <ConditionalButton enabledVariant={this.isNewOrUnsavedCopy ? "outline-secondary" : "outline-danger"} tooltip={this.isNewOrUnsavedCopy ? "Cancel new template" : "Delete template"} display={true} enabled={!this.loading} onEnabledClick={this.onDeleteClick} icon={this.isNewOrUnsavedCopy ? "close" : "bin"}>{this.isNewOrUnsavedCopy ? "Cancel" : "Delete"}</ConditionalButton>
                </Col>
                
                <Col xs="12">
                    <Row>
                        <Col lg="12">
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Template Information</h2>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={this.state.name || ""}
                                            onChange={this.onChange}
                                            onBlur={this.onBlur}
                                            minLength={3}
                                            required={true}
                                            isInvalid={this.state.errors?.name}
                                        />
                                        <Form.Label>NAME</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid name
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <ValueSelect name="category" selected={this.state.category || ""} onChange={this.onChange} options={CATEGORY_OPTIONS} />
                                        <Form.Label>CATEGORY</Form.Label>
                                    </div>
                                </Col>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="caption"
                                            value={this.state.caption || ""}
                                            onChange={this.onChange}
                                            onBlur={this.onBlur}
                                            minLength={3}
                                            required={true}
                                            isInvalid={this.state.errors?.caption}
                                        />
                                        <Form.Label>CAPTION</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid caption
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                                <Row className="g-3">
                                    <Col lg="12">
                                        <h2 className="small-title">Display Circumstances</h2>
                                    </Col>
                                </Row>
                                <Row className="g-3 mb-3">
                                    <Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="allPeopleFlag" label="All People" checked={this.hasFlag(1)} onChange={(e) => this.onFlagChange(e.target.checked, 1)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="leadFlag" label="Leads" checked={this.hasFlag(2)} onChange={(e) => this.onFlagChange(e.target.checked, 2)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="clientFlag" label="Clients" checked={this.hasFlag(4)} onChange={(e) => this.onFlagChange(e.target.checked, 4)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="attorneyFlag" label="Attorneys" checked={this.hasFlag(8)} onChange={(e) => this.onFlagChange(e.target.checked, 8)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="judgeFlag" label="Judges" checked={this.hasFlag(16)} onChange={(e) => this.onFlagChange(e.target.checked, 16)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="staffFlag" label="Staff" checked={this.hasFlag(32)} onChange={(e) => this.onFlagChange(e.target.checked, 32)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="matterDivorceAll" label="All Matters" checked={this.hasFlag(64)} onChange={(e) => this.onFlagChange(e.target.checked, 64)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="matterDivorceFlag" label="Matters (Divorce)" checked={this.hasFlag(128)} onChange={(e) => this.onFlagChange(e.target.checked, 128)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="matterCustodyFlag" label="Matters (Custody)" checked={this.hasFlag(256)} onChange={(e) => this.onFlagChange(e.target.checked, 256)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="matterCSFlag" label="Matters (CS)" checked={this.hasFlag(512)} onChange={(e) => this.onFlagChange(e.target.checked, 512)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="matterNameChangeFlag" label="Matters (Name Change)" checked={this.hasFlag(8192)} onChange={(e) => this.onFlagChange(e.target.checked, 8192)} />
                                    </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                        <Form.Check type="switch" id="matterCivilFlag" label="Matters (Civil)" checked={this.hasFlag(1024)} onChange={(e) => this.onFlagChange(e.target.checked, 1024)} />
                                    </Col>
                                </Row>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="email"
                                            name="from"
                                            value={this.state.from || ""}
                                            onChange={this.onChange}
                                            onBlur={this.onBlur}
                                            minLength={3}
                                            required={true}
                                            isInvalid={this.state.errors?.from}
                                            pattern={EMAIL_PATTERN}
                                        />
                                        <Form.Label>FROM</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid email address
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="email"
                                            name="replyTo"
                                            value={this.state.replyTo || ""}
                                            onChange={this.onChange}
                                            onBlur={this.onBlur}
                                            minLength={3}
                                            required={true}
                                            isInvalid={this.state.errors?.replyTo}
                                            pattern={EMAIL_PATTERN}
                                        />
                                        <Form.Label>REPLY TO</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid email address
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="subject"
                                            value={this.state.subject || ""}
                                            onChange={this.onChange}
                                            onBlur={this.onBlur}
                                            minLength={3}
                                            required={true}
                                            isInvalid={this.state.errors?.subject}
                                        />
                                        <Form.Label>SUBJECT</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid subject
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                            </Row>
                            <CustomFieldsEditor
                                fields={this.state.customFields}
                                errors={this.state.customFieldErrors}
                                onChange={this.onCustomFieldsChange}
                            />
                            <Row>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Editor
                                            value={this.state.html ?? ""}
                                            onValueChange={this.onHtmlChange}
                                            highlight={this.onXmlHighlight}
                                            padding={10}
                                            preClassName="language-xml"
                                            tabSize={2}
                                            insertSpaces
                                            style={EDITOR_STYLE}
                                            textareaClassName="form-control"
                                            className="form-control"
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Criteria for Display</h2>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Editor
                                            value={this.state.criteria ?? ""}
                                            onValueChange={this.onCriteriaChange}
                                            highlight={this.onJsonHighlight}
                                            padding={10}
                                            preClassName="language-json"
                                            textareaClassName="form-control language-json"
                                            tabSize={2}
                                            insertSpaces
                                            style={EDITOR_STYLE}
                                            className="form-control"
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
                <BaseModal show={this.state.modal.delete.show} title={this.state.modal.delete.title} body={this.state.modal.delete.body} buttons={
                    [
                        {
                            caption: "ok",
                            onEnabledClick: this.onDeleteOkModal,
                            variant: "primary",
                            display: true,
                            enabled: true
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onDeleteHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        }
                    ]
                }></BaseModal>
                <TestModal show={this.state.modal.test.show} recipient configuration customFields={this.state.customFields} onOk={this.onTestOkModal} onCancel={(e) => { this.setState(prev => ({ modal: { ...prev.modal, test: { show: false } } })) }} configurations={this.state.configurations} />
                <ScrollToTopButton />
            </>
        )
    }
}

EmailTemplateItem.propTypes = {
    auth: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default withNavigation(connect(mapStateToProps, { pageInfo, loading, getConfiguration, getTemplate, saveTemplate, destroy, sendEmail })(EmailTemplateItem));
